import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { NumberToCurrencyPipe } from '../../../../shared/pipes/number-to-currency.pipe';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { IKioskProduct } from '../../core/interfaces/IKioskProduct';
import { IPackageProduct } from '../../../cart/core/interfaces/IPackageProduct';
import { AddItemsToCart, RemoveItemsFromCart } from '../../../../core/types';
import { cloneDeep } from 'lodash';
import { CartItemTypes } from '../../../cart/core/enums/CartItemTypes';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { CartStore } from '../../../cart/core/stores/CartStore';
import { CartService } from '../../../cart/core/services/CartService';
import { KioskStore } from '../../core/stores/KioskStore';
import { IKioskPackage } from '../../core/interfaces/IKioskPackage';
import { ICartItem } from '../../../cart/core/interfaces/ICartItem';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-convert-products-to-package-modal',
  standalone: true,
  imports: [
    ModalComponent,
    NumberToCurrencyPipe,
    TranslatePipe
  ],
  templateUrl: './convert-products-to-package-modal.component.html',
  styleUrl: './convert-products-to-package-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConvertProductsToPackageModalComponent extends BaseModalComponent<{ convertProductsToPackageData: { item: IKioskProduct; package: IKioskPackage; productsIds: number[]; groups: { groupingNr: number, products: IKioskProduct[]; }[]; }; }> {

  private readonly _cartStore = inject(CartStore);
  private readonly _cartService = inject(CartService);
  private readonly _kioskStore = inject(KioskStore);

  private readonly emptyGroupingNr = -1;

  public confirmConvertToPackage(): void {
    const { package: pck, productsIds, groups } = this.data.convertProductsToPackageData;

    const packageProducts: IPackageProduct[] = [];
    const itemsToDelete: RemoveItemsFromCart = [];
    const itemsToUpdate: ICartItem[] = [];
    const cartItemsClone = cloneDeep(this._cartStore.cart().items);
    const usedPackageContentIndices = new Set<number>();

    productsIds.forEach(id => {
      const fProductIndex = pck.packageContent.findIndex(
        (pc, index) =>
          pc.productId === id &&
          !usedPackageContentIndices.has(index)
      );

      if (fProductIndex !== -1) {
        const fProduct = pck.packageContent[fProductIndex];

        packageProducts.push({
          id: fProduct.productId,
          name: fProduct.product.name,
          groupingNr: fProduct.groupingNr ?? this.emptyGroupingNr
        });

        usedPackageContentIndices.add(fProductIndex);
      }
    });

    packageProducts.forEach(pp => {
      const index = cartItemsClone.findIndex(i => i.itemId === pp.id);

      if (index !== -1) {
        const [item] = cartItemsClone.splice(index, 1);
        const itemNewQuantity = item.quantity - packageProducts
          .filter(pp2 => pp2.id === item.itemId).length;

        item.quantity > 1 && itemNewQuantity > 0
          ? itemsToUpdate.push({ ...item, quantity: itemNewQuantity })
          : itemsToDelete.push({ itemId: item.itemId, position: item.position });
      }
    });

    this._cartService.removeItemFromCart(itemsToDelete)
      .subscribe({
        next: () => {
          this._cartService.updateCartItem(itemsToUpdate)
            .subscribe({
              next: () => {
                const itemToAdd: Partial<ICartItem> = {
                  itemId: pck.id,
                  itemType: CartItemTypes.Package,
                  quantity: 1,
                  packageProducts,
                  flagPackageEditable: !(
                    groups.every(x => x.products.length === 1) ||
                    (groups.length === 1 && groups[0].groupingNr === undefined)
                  )
                };

                this._cartService.addConcessionItemToCart(itemToAdd).subscribe({
                  next: () => {
                    this.data.convertProductsToPackageData = null;
                    this.close();
                  },
                  error: ({ error }: ApiErrorResponse) => {
                    this.notificationService.pushError(
                      'Fehler'.trans('notifications/error.Title'),
                      error.message.trans(`add-package-item-to-cart/notifications/${error.message}.Message`)
                    );
                  }
                });
              },
              error: ({ error }: ApiErrorResponse) => {
                this.notificationService.pushError(
                  'Fehler'.trans('notifications/error.Title'),
                  error.message.trans(`remove-item-from-cart/notifications/${error.message}.Message`)
                );
              }
            });
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`remove-item-from-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public addProductToCart(item: IKioskProduct): void {
    if (this.data.convertProductsToPackageData) {
      this._kioskStore.rejectedPackagesToConvertIds.set(
        [...this._kioskStore.rejectedPackagesToConvertIds(), this.data.convertProductsToPackageData.package.id]
      );
    }

    const itemToAdd: Partial<ICartItem> = {
      itemId: item.id,
      itemType: CartItemTypes.Product,
      quantity: 1
    };

    this._cartService.addConcessionItemToCart(itemToAdd).subscribe({
      error: ({ error }: ApiErrorResponse) => {
        this.notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`add-product-item-to-cart/notifications/${error.message}.Message`)
        );
      }
    });
  }
}
